import { Box } from '@mui/material';
import {
  GridColDef,
  GridColumnGroupingModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { lowerCase } from 'lodash';
import moment, { duration } from 'moment';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ButtonCellComponent,
  EmployeeCellComponent,
  SolaForceDataGrid,
  getFieldNamesFromColumnConfig,
} from '~/components/SolaForceDataGrid';
import { EnumListKey, useAllEnums } from '~/data/enums';
import { AnalyticsReportSettingsProps } from '~/data/reports';
import {
  ReportColumnsVisibility,
  ReportDataExport,
  useReportNavigateToView,
} from '~/scenes/PortalModules/Analytics/common';
import { CompensationEqualityReportEntry } from '~/scenes/PortalModules/Analytics/Rewarding/CompensationEqualityReport/data';

type PropsType = {
  reportSelection: AnalyticsReportSettingsProps<object>;
  isLoading: boolean;
  rows: CompensationEqualityReportEntry[];
  currency: string;
};

export const CompensationEqualityTable = (props: PropsType) => {
  const { reportSelection, isLoading, rows } = props;

  const { t } = useTranslation();

  const { getEnums } = useAllEnums();
  const enumLists = useMemo(() => {
    return {
      frequencyEnums: getEnums(EnumListKey.EVENT_FREQUENCY),
      employeePositionEnums: getEnums(EnumListKey.EMPLOYEE_POSITION),
    };
  }, [getEnums]);

  const { goToEmployeeBasicDataView } = useReportNavigateToView();

  const apiRef = useGridApiRef();

  const { columns, columnGroupingModel } = useMemo(() => {
    const { employeePositionEnums, frequencyEnums } = enumLists;

    const monthlyLabel =
      frequencyEnums.find(({ code }) => code === 'MONTHLY')?.name ?? '';

    const employeeColumns: GridColDef<CompensationEqualityReportEntry>[] = [
      {
        field: 'fEmpId',
        headerName: t('laPicture'),
        filterable: false,
        sortable: false,
        disableExport: true,
        renderCell: (params) => {
          return (
            <EmployeeCellComponent
              {...params}
              pictureOnly={true}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.fEmpId);
              }}
            />
          );
        },
        minWidth: 30,
      },
      {
        field: 'fEmpFirstName',
        headerName: t('laFirstName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.fEmpId);
              }}
            />
          );
        },
        minWidth: 90,
      },
      {
        field: 'fEmpLastName',
        headerName: t('laLastName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.fEmpId);
              }}
            />
          );
        },
        minWidth: 110,
      },
      {
        field: 'fEmpEmployeeNumber',
        headerName: t('laEmpId'),
        minWidth: 130,
      },
      {
        field: 'fEmpGender',
        headerName: t('laGender'),
        type: 'singleSelect',
        valueGetter: ({ value }) =>
          value
            ? value
                .replace('M', t('laMale'))
                .replace('F', t('laFemale'))
                .replace('O', t('laOther'))
            : '',
        valueOptions: [t('laMale'), t('laFemale'), t('laOther')],
      },
      {
        field: 'fEmpDateOfBirth',
        type: 'number',
        headerName: t('laAge'),
        valueGetter: ({ value }) =>
          duration(moment().diff(moment(value, 'YYYY-MM-DD'))).years(),
      },
    ];

    const employmentColumns: GridColDef<CompensationEqualityReportEntry>[] = [
      {
        field: 'fEmpPosition',
        headerName: t('laPosition'),
        valueGetter: ({ value }) =>
          employeePositionEnums.find(({ code }) => code === value)?.name ??
          value,
        minWidth: 180,
      },
      {
        field: 'fEmpJobTitle',
        headerName: t('fEmpJobTitle'),
        minWidth: 180,
      },
      {
        field: 'fEmpTotalDays',
        headerName: t('totalEmploymentTime'),
        valueGetter: ({ value }) => duration(value, 'days'),
        valueFormatter: ({ value }) => {
          const valueDuration = duration(value);
          const years = valueDuration.years();
          const days = valueDuration.subtract(years, 'years').asDays();

          return `${years} ${t('laYearsLK')}, ${days} ${lowerCase(
            t('laDays'),
          )}`;
        },
        minWidth: 160,
      },
      {
        field: 'fEmpTotalPosDays',
        headerName: t('positionEmploymentTime'),
        valueGetter: ({ value }) => duration(value, 'days'),
        valueFormatter: ({ value }) => {
          const valueDuration = duration(value);
          const years = valueDuration.years();
          const days = valueDuration.subtract(years, 'years').asDays();

          return `${years} ${t('laYearsLK')}, ${days} ${lowerCase(
            t('laDays'),
          )}`;
        },
        minWidth: 160,
      },
    ];

    const compensationColumns: GridColDef<CompensationEqualityReportEntry>[] = [
      {
        field: 'salary',
        headerName: t('laBaseSalary'),
        type: 'number',
        valueGetter: ({ row }) => row.aggregated.salary,
        valueFormatter: ({ value }) => value.toString(),
      },
      {
        field: 'allowances',
        headerName: t('laAllowance'),
        type: 'number',
        valueGetter: ({ row }) => row.aggregated.allowance,
        valueFormatter: ({ value }) => value.toString(),
      },
      {
        field: 'benefits',
        headerName: t('laBenefits'),
        type: 'number',
        valueGetter: ({ row }) => row.aggregated.benefits,
        valueFormatter: ({ value }) => value.toString(),
      },
      {
        field: 'total',
        headerName: t('laTotal'),
        type: 'number',
        valueGetter: ({ row }) => row.aggregated.total,
        valueFormatter: ({ value }) => value.toString(),
      },
      {
        field: 'fEmpCurrency',
        headerName: t('laCurrency'),
      },
    ];

    const unitColumns: GridColDef<CompensationEqualityReportEntry>[] = [
      {
        field: 'unitName',
        headerName: t('laOrgUnit'),
        valueGetter: ({ row }) => row.fEmpUnitDetails?.fOrgUnitName ?? '',
        minWidth: 160,
      },
      {
        field: 'unitNumber',
        headerName: t('laUnitNumber'),
        valueGetter: ({ row }) => row.fEmpUnitDetails?.fOrgUnitNumber ?? '',
      },
    ];

    const columnGroupingModel: GridColumnGroupingModel = [
      {
        groupId: 'employee',
        headerName: t('laEmployee'),
        children: getFieldNamesFromColumnConfig(employeeColumns),
      },
      {
        groupId: 'employment',
        headerName: t('laEmployment'),
        children: getFieldNamesFromColumnConfig(employmentColumns),
      },
      {
        groupId: 'compensation',
        headerName: `${t('laCompensation')} (${monthlyLabel})`,
        children: getFieldNamesFromColumnConfig(compensationColumns),
      },
      {
        groupId: 'unit',
        headerName: t('laUnit'),
        children: getFieldNamesFromColumnConfig(unitColumns),
      },
    ];

    return {
      columns: [
        ...employeeColumns,
        ...employmentColumns,
        ...compensationColumns,
        ...unitColumns,
      ],
      columnGroupingModel,
    };
  }, [t, goToEmployeeBasicDataView, enumLists]);

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <ReportColumnsVisibility
          apiRef={apiRef}
          isLoading={isLoading}
          columnGroupingModel={columnGroupingModel}
        />
        <ReportDataExport
          title={t('laCompensationEqualityReport')}
          startDate={reportSelection.params?.startDate}
          apiRef={apiRef}
          isLoading={isLoading}
        />
      </Box>
      <SolaForceDataGrid
        apiRef={apiRef}
        columns={columns}
        rows={rows}
        loading={isLoading}
        density={'compact'}
        getRowId={(row) => row.fEmpId}
        columnGroupingModel={columnGroupingModel}
        experimentalFeatures={{ columnGrouping: true }}
        pinnedColumns={{
          left: ['fEmpId', 'fEmpFirstName', 'fEmpLastName'],
        }}
      />
    </>
  );
};
